import React, { useEffect, useState } from 'react';
import {
    StanAccordion,
    StanAccordionBody,
    StanAccordionHeader,
    StanAccordionItem,
    StanBanner,
    StanButton,
    StanCard,
    StanDivider,
    StanDraggable,
    StanDropdown,
    StanInput,
    StanModal,
    StanTable,
    StanTag,
    StanTextArea,
    StanTipTapEditor,
    StanTooltip,
} from '@/components';
import { DraggableWrapper } from '@/components/core-components/StanDraggable/DraggableWrapper';
import IcReorder from '@/assets/icons/ic-reorder.svg';
import { DropdownValue } from '@/components/core-components/StanDropdown/StanDropdown.types';
import { ButtonColor, ButtonSize, ButtonVariant } from '@/components/core-components/types';
import IconLink from '@/assets/icons/ic-link.svg';
import IcAlertCircle from '@/assets/icons/ic-alert-circle.svg';
import IcInfoCircle from '@/assets/icons/ic-info-circle.svg';
import { STAN_TAG_PRESETS } from '@/lib/constants';
import { useModal } from '@/hooks/useModal';
import { stanNotify } from '@/lib/stanNotify';
import { useDefaultStore, useGlobalStore } from '@/stores';
import { StanCheckbox } from '@/components/core-components/StanCheckbox/StanCheckbox';
import { BadgeSize, BadgeVariant, StanBadge } from '@/components/core-components/StanBadge/StanBadge';

// Draggable Example Component
const DraggableExample = () => {
    const [items, setItems] = useState([
        { id: '1', content: 'Item 1' },
        { id: '2', content: 'Item 2' },
        { id: '3', content: 'Item 3' },
    ]);

    const handleReorder = (event: { allItems: any[] }) => {
        // Reorder the items based on the new order
        const newOrder = event.allItems.map(item => item.id);
        const reorderedItems = newOrder
            .map(id => items.find(item => item.id === id))
            .filter((item): item is NonNullable<typeof item> => Boolean(item));
        setItems(reorderedItems);
    };

    return (
        <StanDraggable onReorder={handleReorder} direction='vertical' gap='sm'>
            {items.map(item => (
                <DraggableWrapper key={item.id} id={item.id}>
                    <div className='bg-canvas border-neutral-soft flex w-full cursor-grab items-center gap-3 rounded-lg border p-3 shadow-sm transition-shadow hover:shadow-md active:cursor-grabbing'>
                        <IcReorder className='text-neutral-dimmed hover:text-neutral-main size-4' />
                        <span className='font-medium'>{item.content}</span>
                    </div>
                </DraggableWrapper>
            ))}
        </StanDraggable>
    );
};

// Disabled Example Component
const DisabledExample = () => {
    const [items, setItems] = useState([
        {
            id: 'disabled-1',
            title: 'Disabled Item 1',
            description: 'This item cannot be dragged',
            backgroundColor: 'bg-brand-subtle',
            textColor: 'text-brand-main',
            disabled: true,
        },
        {
            id: 'disabled-2',
            title: 'Disabled Item 2',
            description: 'Dragging is completely disabled',
            backgroundColor: 'bg-brand-soft',
            textColor: 'text-brand-main',
            disabled: true,
        },
    ]);

    const handleReorder = (event: { allItems: any[] }) => {
        // Reorder the items based on the new order
        const newOrder = event.allItems.map(item => item.id);
        const reorderedItems = newOrder
            .map(id => items.find(item => item.id === id))
            .filter((item): item is NonNullable<typeof item> => Boolean(item));
        setItems(reorderedItems);
    };

    return (
        <StanDraggable onReorder={handleReorder} disabled direction='vertical' gap='sm' className='w-full'>
            {items.map(item => (
                <DraggableWrapper key={item.id} id={item.id} disabled={item.disabled}>
                    <div
                        className={`flex w-full items-center gap-3 rounded-lg p-4 opacity-60 transition-opacity hover:opacity-80 ${item.backgroundColor}`}
                    >
                        {/* Drag Handle */}
                        <div className='text-neutral-dimmed flex size-6 cursor-not-allowed items-center justify-center'>
                            <IcReorder className='size-4' />
                        </div>
                        <div className='flex-1'>
                            <h5 className={`font-medium ${item.textColor}`}>{item.title}</h5>
                            <p className={`text-sm opacity-80 ${item.textColor}`}>{item.description}</p>
                        </div>
                    </div>
                </DraggableWrapper>
            ))}
        </StanDraggable>
    );
};

// Nested Draggables Example Component
const NestedDraggablesExample = () => {
    const [categories, setCategories] = useState([
        {
            id: 'cat-1',
            title: 'Category A',
            items: [
                { id: 'item-1', title: 'Item 1A', backgroundColor: 'bg-brand-subtle', textColor: 'text-brand-main' },
                {
                    id: 'item-2',
                    title: 'Item 2A',
                    backgroundColor: 'bg-success-subtle',
                    textColor: 'text-success-main',
                },
            ],
        },
        {
            id: 'cat-2',
            title: 'Category B',
            items: [
                {
                    id: 'item-3',
                    title: 'Item 1B',
                    backgroundColor: 'bg-warning-subtle',
                    textColor: 'text-warning-main',
                },
                {
                    id: 'item-4',
                    title: 'Item 2B',
                    backgroundColor: 'bg-warning-main',
                    textColor: 'text-warning-dimmed',
                },
            ],
        },
    ]);

    const handleCategoryReorder = (event: { allItems: any[] }) => {
        const newOrder = event.allItems.map(item => item.id);
        const reorderedCategories = newOrder
            .map(id => categories.find(cat => cat.id === id))
            .filter((cat): cat is NonNullable<typeof cat> => Boolean(cat));
        setCategories(reorderedCategories);
    };

    const handleItemReorder = (categoryId: string, event: { allItems: any[] }) => {
        const newOrder = event.allItems.map(item => item.id);
        setCategories(prev =>
            prev.map(cat => {
                if (cat.id === categoryId) {
                    const reorderedItems = newOrder
                        .map(id => cat.items.find(item => item.id === id))
                        .filter((item): item is NonNullable<typeof item> => Boolean(item));
                    return { ...cat, items: reorderedItems };
                }
                return cat;
            })
        );
    };

    return (
        <StanDraggable onReorder={handleCategoryReorder} direction='vertical' gap='md' className='w-full'>
            {categories.map(category => (
                <DraggableWrapper key={category.id} id={category.id}>
                    <div className='border-neutral-soft bg-canvas w-full cursor-grab rounded-lg border p-4 shadow-sm transition-shadow hover:shadow-md active:cursor-grabbing'>
                        <div className='mb-3 flex items-center gap-3'>
                            {/* Drag Handle */}
                            <div className='text-neutral-dimmed flex size-6 items-center justify-center'>
                                <IcReorder className='size-4' />
                            </div>
                            <h3 className='text-neutral-main font-semibold'>{category.title}</h3>
                        </div>
                        <StanDraggable onReorder={e => handleItemReorder(category.id, e)} direction='vertical' gap='sm'>
                            {category.items.map(item => (
                                <DraggableWrapper key={item.id} id={item.id}>
                                    <div
                                        className={`w-full rounded p-3 ${item.backgroundColor} ${item.textColor} flex cursor-grab items-center gap-2 transition-shadow hover:shadow-sm active:cursor-grabbing`}
                                    >
                                        {/* Drag Handle */}
                                        <div className='text-neutral-dimmed hover:text-neutral-main flex size-5 items-center justify-center'>
                                            <IcReorder className='size-3' />
                                        </div>
                                        {item.title}
                                    </div>
                                </DraggableWrapper>
                            ))}
                        </StanDraggable>
                    </div>
                </DraggableWrapper>
            ))}
        </StanDraggable>
    );
};

type PresetType = keyof typeof STAN_TAG_PRESETS;

export default function InternalsPage() {
    const setBreadcrumbs = useGlobalStore(state => state.setBreadcrumbs);
    const { storeId, theme, primaryColor, sections, pages, landingPages } = useDefaultStore();
    const [variant, setVariant] = useState<DropdownValue | null>(null);
    const [color, setColor] = useState<DropdownValue | null>(null);
    const [size, setSize] = useState<DropdownValue | null>(null);
    const [textAreaValue, setTextAreaValue] = useState('');
    const [textAreaError, setTextAreaError] = useState(false);
    const [textAreaLabel, setTextAreaLabel] = useState('');
    const [textAreaCaption, setTextAreaCaption] = useState('');
    const [textAreaDisabled, setTextAreaDisabled] = useState(false);
    const [tooltipPosition, setTooltipPosition] = useState<DropdownValue>({ label: 'Top', value: 'top' });
    const [tooltipTrigger, setTooltipTrigger] = useState<DropdownValue>({ label: 'Hover', value: 'hover' });
    const [tooltipType, setTooltipType] = useState<DropdownValue>({ label: 'Subtle', value: 'subtle' });
    const [tooltipHideArrow, setTooltipHideArrow] = useState<DropdownValue>({ label: 'Show', value: 'false' });
    const [tooltipText, setTooltipText] = useState<string>("I'm a tooltip");
    const [dividerThick, setDividerThick] = useState<DropdownValue>({ label: 'No', value: 'false' });
    const [dividerPadded, setDividerPadded] = useState<DropdownValue>({ label: 'No', value: 'false' });
    const [dividerLabel, setDividerLabel] = useState('');

    // For StanInput playground
    const [inputRegular, setInputRegular] = useState('Regular');
    const [inputHint, setInputHint] = useState('Hint');
    const [inputLabel, setInputLabel] = useState('Label');
    const [inputError, setInputError] = useState('Negative');
    const [inputErrorHint, setInputErrorHint] = useState('Negative with hint');

    // For StanModal playground
    const { isOpen: isOpen1, open: open1, close: close1 } = useModal();
    const { isOpen: isOpen2, open: open2, close: close2 } = useModal();
    const { isOpen: isOpen3, open: open3, close: close3 } = useModal();
    const { isOpen: isOpen4, open: open4, close: close4 } = useModal();
    const [modalActionLoading, setModalActionLoading] = useState(false);

    // For StanTag playground
    const [stanTagType, setStanTagType] = useState<PresetType>('product');
    const [stanTagValue, setStanTagValue] = useState<string>('lead');
    const [stanTagLabel, setStanTagLabel] = useState('');
    const [stanTagBg, setStanTagBg] = useState('');
    const [stanTagText, setStanTagText] = useState('');
    const [showSvgAsset, setShowSvgAsset] = useState(true);
    const [showRemoveSvgFill, setShowRemoveSvgFill] = useState(true);
    const [showImageString, setShowImageString] = useState(false);

    const stanTagTypeOptions = Object.keys(STAN_TAG_PRESETS).map(type => ({ label: type, value: type }));
    const stanTagValueOptions =
        stanTagType && STAN_TAG_PRESETS[stanTagType]
            ? Object.keys(STAN_TAG_PRESETS[stanTagType]).map(val => ({ label: val, value: val }))
            : [];

    // For StanCheckbox
    const [stanCheckboxError, setStanCheckboxError] = useState<DropdownValue | null>(null);
    const [stanCheckboxLoading, setStanCheckboxLoading] = useState<DropdownValue | null>(null);
    const [stanCheckboxDisabled, setStanCheckboxDisabled] = useState<DropdownValue | null>(null);
    const [stanCheckboxIndeterminate, setStanCheckboxIndeterminate] = useState<DropdownValue | null>(null);
    const handleStanCheckboxError = (value: DropdownValue) => {
        setStanCheckboxError(value);
    };
    const handleStanCheckboxLoading = (value: DropdownValue) => {
        setStanCheckboxLoading(value);
    };
    const handleStanCheckboxDisabled = (value: DropdownValue) => {
        setStanCheckboxDisabled(value);
    };
    const handleStanCheckboxIndeterminate = (value: DropdownValue) => {
        setStanCheckboxIndeterminate(value);
    };
    const [checkValue, setCheckValue] = useState(false);
    const [checkValueRight, setCheckValueRight] = useState(false);
    const [checkValueRightCaption, setCheckValueRightCaption] = useState(false);
    const [checkValueLeft, setCheckValueLeft] = useState(false);
    const [checkValueLeftCaption, setCheckValueLeftCaption] = useState(false);

    // For StanBadge playground
    const [badgeVariant, setBadgeVariant] = useState<DropdownValue>({ label: 'Default', value: 'default' });
    const [badgeSize, setBadgeSize] = useState<DropdownValue>({ label: 'Medium', value: 'md' });
    const [badgeText, setBadgeText] = useState('Badge');
    const [badgeWithIcon, setBadgeWithIcon] = useState(false);
    const [badgeClickable, setBadgeClickable] = useState(false);

    // Set breadcrumbs for internals section
    useEffect(() => {
        setBreadcrumbs([
            { title: 'Home', to: '/dashboard', id: 'home' },
            { title: 'Internals', to: '/internals', active: true, id: 'internals' },
        ]);
    }, [setBreadcrumbs]);

    const handleVariantSelect = (value: DropdownValue) => {
        setVariant(value);
    };

    const handleColorSelect = (value: DropdownValue) => {
        setColor(value);
    };

    const handleSizeSelect = (value: DropdownValue) => {
        setSize(value);
    };

    const validateTextArea = (value: string) => {
        if (value.length === 0) return '';
        if (value.toLowerCase() === value) {
            return "Text can't be all lowercase";
        }
        return '';
    };

    const handleTextAreaChange = (value: string) => {
        setTextAreaValue(value);
        setTextAreaError(!!validateTextArea(value));
    };

    // StanTable playground
    const [page, setPage] = useState(1);
    const pageSize = 10;
    const [isAudienceLoading, setIsAudienceLoading] = useState(true);
    const [tableTextFilter, setTextFilter] = useState('');

    // Mock network request
    useEffect(() => {
        const timer = setTimeout(() => {
            setIsAudienceLoading(false);
        }, 1000);
        return () => clearTimeout(timer);
    }, []);

    // Filtered data based on name and email
    const filteredAudienceData = React.useMemo(() => {
        if (!tableTextFilter.trim()) return mockDataForTable;
        return mockDataForTable.filter(
            row =>
                row.email.toLowerCase().includes(tableTextFilter.trim().toLowerCase()) ||
                row.fullName.toLowerCase().includes(tableTextFilter.trim().toLowerCase())
        );
    }, [tableTextFilter]);

    const totalPages = Math.ceil(filteredAudienceData.length / pageSize) || 1;
    const pagedAudienceData = filteredAudienceData.slice((page - 1) * pageSize, page * pageSize);

    React.useEffect(() => {
        if (page > totalPages) setPage(1);
    }, [page, tableTextFilter, totalPages]);

    // TipTap thingies
    const [tipTapValue, setTipTapValue] = useState('');

    // AI Suggestion handlers
    const generateAiSuggestion = async (prompt: string) => {
        // Simulate AI generation
        return new Promise<string>(resolve => {
            setTimeout(() => {
                const mockSuggestion = `<p><strong>AI Generated Content:</strong> This is a sample AI-generated suggestion that demonstrates the new AI suggestion block functionality. It includes <em>formatted text</em> and shows how the component handles HTML content.</p><p>You can accept this suggestion to replace your current content, or dismiss it to keep your original text.</p> <p>Here is your prompt: ${prompt} </p>`;
                resolve(mockSuggestion);
            }, 2000);
        });
    };

    const internalsContent = (
        <div className='flex flex-col items-center pt-4'>
            <div className='mb-6'>
                <h2>Internals</h2>
                <div className='mt-4 flex gap-4'>
                    <StanButton
                        name='internals-components-link'
                        color='primary'
                        variant='outline'
                        size='sm'
                        onClick={() => (window.location.href = '/internals/components')}
                    >
                        Components Showcase
                    </StanButton>
                </div>
            </div>
            <h2>Playground</h2>
            <div className='mt-5 flex w-full flex-col'>
                <section className='mt-12 w-full'>
                    <h2>TipTap Editor</h2>
                    <div className='mb-4 space-y-2'>
                        <div className='flex items-center gap-4'>
                            <span className='text-sm'>Store ID: {storeId || 'Not set'}</span>
                        </div>

                        {storeId && (
                            <div className='bg-neutral-subtle space-y-1 rounded p-3 text-xs'>
                                <div>
                                    <strong>Theme:</strong> {theme}
                                </div>
                                <div>
                                    <strong>Primary Color:</strong> {primaryColor || 'Not set'}
                                </div>
                                <div>
                                    <strong>Sections:</strong> {sections.length} sections
                                </div>
                                <div>
                                    <strong>Pages:</strong> {pages.length} pages
                                </div>
                                <div>
                                    <strong>Landing Pages:</strong> {landingPages.length} landing pages
                                </div>
                            </div>
                        )}
                    </div>
                    <StanTipTapEditor
                        value={tipTapValue}
                        onChange={setTipTapValue}
                        className='mb-4'
                        personalizeItemData={[
                            {
                                name: 'My Username',
                                id: 'my_username',
                                required: false,
                            },
                            {
                                name: 'My Name',
                                id: 'my_name',
                                required: false,
                            },
                        ]}
                        onAiGenerate={generateAiSuggestion}
                    />
                </section>
                {/* Tables Section */}
                <div className='bg-canvas mb-8 w-full rounded-lg border-2 border-b-4 border-l-4 p-6 shadow-md'>
                    <section id='tables' className='w-full'>
                        <h3>Tables</h3>

                        <section className='mt-2 flex w-full flex-col gap-4' />
                        <StanCard>
                            <StanTable<AudienceRow>
                                title='Customers'
                                data={pagedAudienceData}
                                page={page}
                                totalPages={totalPages}
                                onPageChange={setPage}
                                isLoading={isAudienceLoading}
                                onRowClick={row => {
                                    stanNotify({
                                        title: 'Row Clicked',
                                        text: `${row.id} - ${row.fullName}`,
                                        type: 'error',
                                        duration: 2000,
                                    });
                                }}
                                columns={[
                                    {
                                        header: 'Name',
                                        accessorKey: 'fullName',
                                        width: '20%',
                                    },
                                    {
                                        header: 'Email',
                                        accessorKey: 'email',
                                        width: '20%',
                                        hideOnMobile: true,
                                    },
                                    {
                                        header: 'Since',
                                        accessorKey: 'fanSinceDate',
                                        hideOnMobile: true,
                                        width: '15%',
                                        cell: info => {
                                            const date = new Date(info.getValue());
                                            return date.toLocaleDateString('en-US', {
                                                year: 'numeric',
                                                month: 'long',
                                                day: 'numeric',
                                            });
                                        },
                                    },
                                    {
                                        header: 'Purchases',
                                        accessorKey: 'totalPurchases',
                                        width: '10%',
                                        cell: info => {
                                            const value = info.getValue();
                                            const isMany = value > 50;
                                            return (
                                                <span
                                                    style={
                                                        isMany
                                                            ? {
                                                                  color: 'var(--stan-text-positive-color)',
                                                                  fontWeight: 900,
                                                              }
                                                            : {}
                                                    }
                                                >
                                                    {value}
                                                </span>
                                            );
                                        },
                                        hideOnMobile: true,
                                        align: 'right',
                                    },
                                    {
                                        header: 'Spent',
                                        accessorKey: 'totalSpent',
                                        width: '15%',
                                        cell: info => (
                                            <span className='text-success-main'>
                                                {new Intl.NumberFormat('en-US', {
                                                    style: 'currency',
                                                    currency: 'USD',
                                                }).format(info.getValue())}
                                            </span>
                                        ),
                                        align: 'right',
                                    },
                                    {
                                        header: '',
                                        accessorKey: 'actions',
                                        width: '20%',
                                        hideOnMobile: true,
                                    },
                                ]}
                                emptyHeader={tableTextFilter ? 'No Matches Found' : 'No Data To Show Yet'}
                                emptySubheader={
                                    tableTextFilter
                                        ? `'${tableTextFilter}' returned no entries`
                                        : 'Learn how to improve your business metrics <a href="https://help.stan.store/article/140-how-to-make-my-first-sale-on-stan-w-statistics" target="_blank" rel="noopener noreferrer">here</a>!'
                                }
                                totalRowsLabel={`${filteredAudienceData.length} customers`}
                                filterValue={tableTextFilter}
                                onFilterChange={setTextFilter}
                            />
                        </StanCard>
                    </section>
                </div>

                {/* Accordions Section */}
                <div className='bg-canvas mb-8 w-full rounded-lg border-2 border-b-4 border-l-4 p-6 shadow-md'>
                    <section id='accordions' className='w-full'>
                        <h2>Accordions</h2>
                        <div className='mt-5 grid grid-cols-2 gap-4'>
                            <div>
                                <h3>Accordion list</h3>
                                <StanAccordion>
                                    <StanAccordionItem id='accordion-1'>
                                        <StanAccordionHeader>What even is an accordion?</StanAccordionHeader>
                                        <StanAccordionBody>
                                            <p>
                                                Accordion can be used to show or hide content. It is a great way to
                                                organize content in a compact way. You can have multiple accordions open
                                                at the same time or only one at a time, depending on your use case.
                                            </p>
                                            <p>Oh, this is also an accordion.</p>
                                            <img
                                                src='https://thumbs.static-thomann.de/thumb/padthumb600x600/pics/bdb/_51/513165/16524601_800.jpg'
                                                alt=''
                                            />
                                        </StanAccordionBody>
                                    </StanAccordionItem>
                                    <StanAccordionItem id='accordion-2'>
                                        <StanAccordionHeader>Header 2</StanAccordionHeader>
                                        <StanAccordionBody>Body 2</StanAccordionBody>
                                    </StanAccordionItem>
                                </StanAccordion>
                            </div>
                            <div>
                                <h3>Accordion list - Cards</h3>

                                <StanAccordion allowMultipleOpen className='flex flex-col gap-4'>
                                    <StanAccordionItem isCard id='accordion-card-1' startOpen>
                                        <StanAccordionHeader hideEndIcon startIcon={IconLink}>
                                            <div className='flex w-full items-center justify-between'>
                                                <h6 className='text-lg'>My new flow to test</h6>
                                                <span className='label text'>CA$0.00</span>
                                                <span className='label text'>2</span>
                                                <span className='label text'>100% (2)</span>
                                                <span className='label text'>0.0% (0)</span>
                                                <StanTag label='Draft' />
                                            </div>
                                        </StanAccordionHeader>
                                        <StanAccordionBody>Body 1</StanAccordionBody>
                                    </StanAccordionItem>

                                    <StanAccordionItem isCard id='accordion-card-2'>
                                        <StanAccordionHeader>
                                            This is a list of different accordions
                                        </StanAccordionHeader>
                                        <StanAccordionBody>
                                            <StanAccordion>
                                                <StanAccordionHeader>
                                                    I am a nested accordion header
                                                </StanAccordionHeader>
                                                <StanAccordionBody>
                                                    <p className='text-sm'>This is a nested accordion</p>
                                                    <StanAccordion>
                                                        <StanAccordionItem id='nested-accordion-1'>
                                                            <StanAccordionHeader>Nested Header 1</StanAccordionHeader>
                                                            <StanAccordionBody>Nested Body 1</StanAccordionBody>
                                                        </StanAccordionItem>
                                                        <StanAccordionItem id='nested-accordion-2'>
                                                            <StanAccordionHeader>Nested Header 2</StanAccordionHeader>
                                                            <StanAccordionBody>Nested Body 2</StanAccordionBody>
                                                        </StanAccordionItem>
                                                    </StanAccordion>
                                                </StanAccordionBody>
                                            </StanAccordion>
                                        </StanAccordionBody>
                                    </StanAccordionItem>
                                </StanAccordion>
                            </div>
                        </div>
                    </section>
                </div>

                {/* Modals Section */}
                <div className='bg-canvas mb-8 w-full rounded-lg border-2 border-b-4 border-l-4 p-6 shadow-md'>
                    <section id='modals' className='w-full'>
                        <h3>Modals</h3>
                        <div className='mt-2 flex items-center justify-between gap-2'>
                            <StanModal
                                id='test1'
                                isOpen={isOpen1}
                                onClose={close1}
                                title='Scrollable modal'
                                image='https://framerusercontent.com/images/NWCNqPGvu8naiZHNqJBEMnsG0o.png'
                                largeImage
                                primaryActionLabel='Primary Action'
                                onPrimaryAction={() => {
                                    close1();
                                }}
                                secondaryActionLabel='Cancel'
                                onSecondaryAction={() => {
                                    close1();
                                }}
                            >
                                <div className='flex w-full flex-col gap-4'>
                                    <p className='text-sm'>
                                        Lorem ipsum dolor sit amet consectetur, adipisicing elit. Ab quas enim
                                        architecto? Esse eaque saepe, recusandae corporis tempore distinctio? Ipsam
                                        impedit consectetur similique. Culpa adipisci reiciendis excepturi odio, quam
                                        ipsa suscipit ipsam nihil iure similique voluptate, dolorum quo quos cumque
                                        quidem assumenda. Reiciendis corporis voluptates, repellat quasi maxime rerum
                                        doloribus!
                                    </p>
                                    <p className='text-sm'>
                                        Lorem ipsum dolor sit amet consectetur, adipisicing elit. Error unde quia iste
                                        facere dicta aperiam porro explicabo ducimus eius quasi vitae, veritatis ipsum
                                        et dignissimos deserunt a nihil amet rem atque voluptate officiis similique ad
                                        placeat quo. Rem, ad.
                                    </p>
                                    <p className='text-sm'>
                                        Lorem ipsum dolor sit amet consectetur, adipisicing elit. Ab quas enim
                                        architecto? Esse eaque saepe, recusandae corporis tempore distinctio? Ipsam
                                        impedit consectetur similique. Culpa adipisci reiciendis excepturi odio, quam
                                        ipsa suscipit ipsam nihil iure similique voluptate, dolorum quo quos cumque
                                        quidem assumenda. Reiciendis corporis voluptates, repellat quasi maxime rerum
                                        doloribus!
                                    </p>
                                    <p className='text-sm'>
                                        Lorem ipsum dolor sit amet consectetur, adipisicing elit. Error unde quia iste
                                        facere dicta aperiam porro explicabo ducimus eius quasi vitae, veritatis ipsum
                                        et dignissimos deserunt a nihil amet rem atque voluptate officiis similique ad
                                        placeat quo. Rem, ad.
                                    </p>
                                    <p className='text-sm'>
                                        Lorem ipsum dolor sit amet consectetur, adipisicing elit. Ab quas enim
                                        architecto? Esse eaque saepe, recusandae corporis tempore distinctio? Ipsam
                                        impedit consectetur similique. Culpa adipisci reiciendis excepturi odio, quam
                                        ipsa suscipit ipsam nihil iure similique voluptate, dolorum quo quos cumque
                                        quidem assumenda. Reiciendis corporis voluptates, repellat quasi maxime rerum
                                        doloribus!
                                    </p>
                                </div>
                            </StanModal>
                            <StanModal
                                id='test2'
                                isOpen={isOpen2}
                                onClose={close2}
                                title="What's the name of your team?"
                                nonDismissible
                                image='https://framerusercontent.com/images/NWCNqPGvu8naiZHNqJBEMnsG0o.png'
                                primaryActionLoading={modalActionLoading}
                                primaryActionLabel='Save'
                                onPrimaryAction={() => {
                                    setModalActionLoading(true);
                                    setTimeout(() => {
                                        setModalActionLoading(false);
                                        close2();
                                    }, 2000);
                                }}
                                secondaryActionDisabled={false}
                                secondaryActionLabel='Cancel'
                                onSecondaryAction={() => {
                                    close2();
                                }}
                            >
                                <div className='flex w-full flex-col gap-4'>
                                    <StanInput
                                        id='team-name'
                                        name='team-name'
                                        value=''
                                        onChange={() => {}}
                                        autoComplete='off'
                                        placeholder='Enter your team name'
                                    />
                                </div>
                            </StanModal>
                            <StanModal
                                id='test3'
                                isOpen={isOpen3}
                                onClose={close3}
                                title='Oops! Something went wrong...'
                                primaryActionDisabled={false}
                            >
                                <p className='text-primary text-center'>Please try again later</p>
                            </StanModal>
                            <StanModal
                                id='test4'
                                isOpen={isOpen4}
                                onClose={close4}
                                primaryActionDisabled={false}
                                title='Introducing Virtual Reality'
                                hideCloseButton
                                primaryActionColor='destructive'
                                primaryActionLabel='Delete'
                                onPrimaryAction={() => {
                                    close4();
                                }}
                                secondaryActionDisabled={false}
                                onSecondaryAction={() => {
                                    close4();
                                }}
                            >
                                <p className='text-primary text-center'>Some truly cool, inspiring, content</p>
                            </StanModal>
                            <StanButton name='modal-btn-1' variant='outline' size='xs' onClick={open1}>
                                Scrollable content
                            </StanButton>
                            <StanButton name='modal-btn-2' variant='outline' size='xs' onClick={open2}>
                                Non dismissable
                            </StanButton>
                            <StanButton name='modal-btn-3' variant='outline' size='xs' onClick={open3}>
                                No image, no actions
                            </StanButton>
                            <StanButton
                                name='modal-btn-4'
                                variant='outline'
                                size='xs'
                                color='destructive'
                                onClick={open4}
                            >
                                Destructive
                            </StanButton>
                        </div>
                    </section>
                </div>

                {/* Tags Section */}
                <div className='bg-canvas mb-8 w-full rounded-lg border-2 border-b-4 border-l-4 p-6 shadow-md'>
                    <section id='tags' className='w-full'>
                        <h3>App Tag</h3>
                        <div className='mb-4 flex flex-wrap gap-2'>
                            <StanDropdown
                                id='stantag-type'
                                name='stantag-type'
                                options={stanTagTypeOptions}
                                onSelect={v => setStanTagType(v.value as PresetType)}
                                label='Type'
                            />
                            <StanDropdown
                                id='stantag-value'
                                name='stantag-value'
                                options={stanTagValueOptions}
                                onSelect={v => setStanTagValue(v.value)}
                                label='Value'
                            />
                            <StanInput
                                id='stantag-label'
                                name='stantag-label'
                                value={stanTagLabel}
                                onChange={e => setStanTagLabel(e.target.value)}
                                topLabel='Label (optional)'
                            />
                            <StanInput
                                id='stantag-bg'
                                name='stantag-bg'
                                value={stanTagBg}
                                onChange={e => setStanTagBg(e.target.value)}
                                topLabel='Background Color (optional)'
                                placeholder='#0084C6 or rgba(0,0,0,0.1)'
                            />
                            <StanInput
                                id='stantag-text'
                                name='stantag-text'
                                value={stanTagText}
                                onChange={e => setStanTagText(e.target.value)}
                                topLabel='Text Color (optional)'
                                placeholder='#fff or red'
                            />
                            <div>
                                <label className='flex items-center gap-1'>
                                    <input
                                        type='checkbox'
                                        checked={showSvgAsset}
                                        onChange={e => setShowSvgAsset(e.target.checked)}
                                    />
                                    Svg Asset
                                </label>
                                <label className='flex items-center gap-1'>
                                    <input
                                        type='checkbox'
                                        checked={showRemoveSvgFill}
                                        onChange={e => setShowRemoveSvgFill(e.target.checked)}
                                    />
                                    Remove Fill
                                </label>
                                <label className='flex items-center gap-1'>
                                    <input
                                        type='checkbox'
                                        checked={showImageString}
                                        onChange={e => setShowImageString(e.target.checked)}
                                    />
                                    Image (URL)
                                </label>
                            </div>
                        </div>

                        <div className='mt-4'>
                            <h4>Custom tag</h4>
                            <div className='flex flex-wrap gap-2'>
                                <StanTooltip content={`Tooltip for '${stanTagLabel || stanTagValue}'`}>
                                    <StanTag
                                        label={stanTagLabel}
                                        // style={{
                                        //     backgroundColor: stanTagBg,
                                        //     color: stanTagText,
                                        // }}
                                        className='bg-positive-subtle text-positive-fg-positive-bold'
                                        {...(showSvgAsset ? { svgAsset: IconLink } : {})}
                                        imageString={
                                            showImageString
                                                ? 'https://s3-eu-west-1.amazonaws.com/tpd/logos/625839e371517a6c7c797fdb/0x0.png'
                                                : undefined
                                        }
                                    />
                                </StanTooltip>
                            </div>
                        </div>
                        <div className='mt-4'>
                            <h4>Filtered presets</h4>
                            <div className='flex flex-wrap gap-2'>
                                {STAN_TAG_PRESETS[stanTagType] &&
                                    (
                                        Object.entries(STAN_TAG_PRESETS[stanTagType]) as [
                                            string,
                                            { label: string; backgroundColor: string; textColor: string },
                                        ][]
                                    ).map(([val, preset]) => <StanTag key={val} label={preset.label} />)}
                            </div>
                        </div>
                    </section>
                </div>

                {/* TextArea Section */}
                <div className='bg-canvas mb-8 w-full rounded-lg border-2 border-b-4 border-l-4 p-6 shadow-md'>
                    <section id='textareas'>
                        <h3>TextArea</h3>
                        <div className='my-5 flex w-full items-center gap-2'>
                            <StanInput
                                id='textarea-label'
                                name='textarea-label'
                                value={textAreaLabel}
                                placeholder='Text Area label'
                                onChange={e => setTextAreaLabel(e.target.value)}
                            />
                            <StanInput
                                id='textarea-caption'
                                name='textarea-caption'
                                value={textAreaCaption}
                                placeholder='Text Area caption'
                                onChange={e => setTextAreaCaption(e.target.value)}
                            />

                            <label htmlFor='textarea-disabled' className='flex items-center gap-1'>
                                <input
                                    type='checkbox'
                                    id='textarea-disabled'
                                    checked={textAreaDisabled}
                                    onChange={e => setTextAreaDisabled(e.target.checked)}
                                />
                                Disabled
                            </label>
                        </div>
                        <StanTextArea
                            id='textarea-1'
                            name='textarea-1'
                            label={textAreaLabel}
                            hintText={textAreaError ? validateTextArea(textAreaValue) : textAreaCaption}
                            value={textAreaValue}
                            onChange={e => handleTextAreaChange(e.target.value)}
                            error={!!textAreaError}
                            placeholder="What's on your mind?"
                            disabled={textAreaDisabled}
                        />
                    </section>
                </div>

                {/* Tooltips Section */}
                <div className='bg-canvas mb-8 w-full rounded-lg border-2 border-b-4 border-l-4 p-6 shadow-md'>
                    <section id='tooltips'>
                        <h3>Tooltips</h3>
                        <div className='grid grid-cols-2 gap-4'>
                            <StanDropdown
                                id='tooltip-position'
                                name='tooltip-position'
                                options={[
                                    { label: 'Top', value: 'top' },
                                    { label: 'Bottom', value: 'bottom' },
                                    { label: 'Left', value: 'left' },
                                    { label: 'Right', value: 'right' },
                                ]}
                                onSelect={setTooltipPosition}
                                label='Position'
                            />
                            <StanDropdown
                                id='tooltip-trigger'
                                name='tooltip-trigger'
                                options={[
                                    { label: 'Hover', value: 'hover' },
                                    { label: 'Click', value: 'click' },
                                ]}
                                onSelect={setTooltipTrigger}
                                label='Trigger'
                            />
                            <StanDropdown
                                id='tooltip-type'
                                name='tooltip-type'
                                options={[
                                    { label: 'Subtle', value: 'subtle' },
                                    { label: 'Contrast', value: 'contrast' },
                                    { label: 'Accent', value: 'accent' },
                                ]}
                                onSelect={setTooltipType}
                                label='Type'
                            />
                            <StanDropdown
                                id='tooltip-hide-arrow'
                                name='tooltip-hide-arrow'
                                options={[
                                    { label: 'Show', value: 'false' },
                                    { label: 'Hide', value: 'true' },
                                ]}
                                onSelect={setTooltipHideArrow}
                                label='Hide Arrow'
                            />
                            <StanInput
                                id='tooltip-text'
                                name='tooltip-text'
                                value={tooltipText}
                                onChange={e => setTooltipText(e.target.value)}
                                topLabel='Tooltip Text'
                            />
                            <span className='m-auto mb-0'>
                                <StanTooltip
                                    position={tooltipPosition.value as 'top' | 'bottom' | 'left' | 'right'}
                                    trigger={tooltipTrigger.value as 'hover' | 'click'}
                                    type={tooltipType.value as 'subtle' | 'contrast' | 'accent'}
                                    content={tooltipText}
                                    hideArrow={tooltipHideArrow.value === 'true'}
                                >
                                    <StanButton name='tooltip-test-button' onClick={() => {}} type='button'>
                                        Tooltip activator!
                                    </StanButton>
                                </StanTooltip>
                            </span>
                        </div>
                    </section>
                </div>

                {/* Dividers Section */}
                <div className='bg-canvas mb-8 w-full rounded-lg border-2 border-b-4 border-l-4 p-6 shadow-md'>
                    <section id='dividers' className='w-full'>
                        <h3>Dividers</h3>
                        <div className='mb-4 grid grid-cols-2 gap-4'>
                            <StanDropdown
                                id='divider-thick'
                                name='divider-thick'
                                options={[
                                    { label: 'Thin', value: 'false' },
                                    { label: 'Thick', value: 'true' },
                                ]}
                                onSelect={setDividerThick}
                                label='Thickness'
                            />
                            <StanDropdown
                                id='divider-padded'
                                name='divider-padded'
                                options={[
                                    { label: 'No', value: 'false' },
                                    { label: 'Yes', value: 'true' },
                                ]}
                                onSelect={setDividerPadded}
                                label='Padded'
                            />
                        </div>
                        <div className='mb-4'>
                            <StanInput
                                id='divider-label'
                                name='divider-label'
                                value={dividerLabel}
                                onChange={e => setDividerLabel(e.target.value)}
                                topLabel='Custom Label'
                                autoComplete='off'
                                placeholder='Add label to divider'
                            />
                        </div>
                        <StanDivider
                            thick={dividerThick.value === 'true'}
                            padded={dividerPadded.value === 'true'}
                            label={dividerLabel || undefined}
                        />
                    </section>
                </div>

                {/* Inputs Section */}
                <div className='bg-canvas mb-8 w-full rounded-lg border-2 border-b-4 border-l-4 p-6 shadow-md'>
                    <section id='inputs'>
                        <h3>Inputs</h3>
                        <div className='mt-3 grid grid-cols-2 gap-x-6 gap-y-4'>
                            <StanInput
                                id='input-regular'
                                name='input-regular'
                                onChange={e => setInputRegular(e.target.value)}
                                value={inputRegular}
                            />
                            <StanInput
                                id='input-disabled'
                                name='input-disabled'
                                value='Disabled'
                                onChange={() => {}}
                                disabled
                            />
                            <StanInput
                                id='input-hint'
                                name='input-hint'
                                value={inputHint}
                                onChange={e => setInputHint(e.target.value)}
                                hintText='Hint text'
                            />
                            <StanInput
                                id='input-label'
                                name='input-label'
                                value={inputLabel}
                                onChange={e => setInputLabel(e.target.value)}
                                topLabel='Label'
                            />
                            <StanInput
                                id='input-error'
                                name='input-error'
                                value={inputError}
                                onChange={e => setInputError(e.target.value)}
                                topLabel='Label'
                                error
                            />
                            <StanInput
                                id='input-error-hint'
                                name='input-error-hint'
                                value={inputErrorHint}
                                onChange={e => setInputErrorHint(e.target.value)}
                                topLabel='Label'
                                hintText='Hint text'
                                error
                            />
                        </div>
                    </section>
                </div>

                {/* Buttons Section */}
                <div className='bg-canvas mb-8 w-full rounded-lg border-2 border-b-4 border-l-4 p-6 shadow-md'>
                    <section id='buttons'>
                        <h3>Buttons</h3>
                        <div className='flex flex-col gap-y-4'>
                            <div className='grid grid-cols-2 gap-4'>
                                <StanDropdown
                                    id='button-variant-dropdown'
                                    name='button-variant-dropdown'
                                    options={[
                                        { label: 'Default (no variant)', value: '' },
                                        { label: 'Ghost', value: 'ghost' },
                                        { label: 'Square', value: 'square' },
                                        { label: 'Outline', value: 'outline' },
                                        { label: 'Link', value: 'link' },
                                    ]}
                                    onSelect={handleVariantSelect}
                                    label='Variant'
                                />
                                <StanDropdown
                                    id='button-color-dropdown'
                                    name='button-color-dropdown'
                                    options={[
                                        { label: 'Primary', value: 'primary' },
                                        { label: 'Secondary', value: 'secondary' },
                                        { label: 'Dark', value: 'dark' },
                                        { label: 'Destructive', value: 'destructive' },
                                    ]}
                                    onSelect={handleColorSelect}
                                    label='Color'
                                />
                                <StanDropdown
                                    id='button-size-dropdown'
                                    name='button-size-dropdown'
                                    options={[
                                        { label: 'XS', value: 'xs' },
                                        { label: 'SM', value: 'sm' },
                                        { label: 'MD', value: 'md' },
                                        { label: 'LG', value: 'lg' },
                                        { label: 'XL', value: 'xl' },
                                    ]}
                                    onSelect={handleSizeSelect}
                                    label='Size'
                                />
                            </div>

                            <div className='mt-4 flex flex-wrap items-center gap-4'>
                                <StanButton
                                    name='test-button-normal'
                                    color={(color?.value as ButtonColor) || 'primary'}
                                    variant={variant?.value ? (variant.value as ButtonVariant) : undefined}
                                    size={(size?.value as ButtonSize) || 'md'}
                                    onClick={() => {
                                        stanNotify({
                                            title: 'Button Clicked',
                                            text: `Variant: ${variant?.value || 'default'}, Color: ${color?.value || 'primary'}, Size: ${size?.value || 'md'}`,
                                            type: 'success',
                                        });
                                    }}
                                    type='button'
                                >
                                    Normal Button
                                </StanButton>

                                <StanButton
                                    name='test-button-with-icon'
                                    color={(color?.value as ButtonColor) || 'primary'}
                                    variant={variant?.value ? (variant.value as ButtonVariant) : undefined}
                                    size={(size?.value as ButtonSize) || 'md'}
                                    onClick={() => {
                                        stanNotify({
                                            title: 'Icon Button Clicked',
                                            type: 'info',
                                        });
                                    }}
                                    icon={IconLink}
                                    type='button'
                                >
                                    With Icon
                                </StanButton>

                                <StanButton
                                    name='test-button-loading'
                                    color={(color?.value as ButtonColor) || 'primary'}
                                    variant={variant?.value ? (variant.value as ButtonVariant) : undefined}
                                    size={(size?.value as ButtonSize) || 'md'}
                                    onClick={() => {}}
                                    type='button'
                                    loading
                                >
                                    Loading State
                                </StanButton>

                                <StanButton
                                    name='test-button-disabled'
                                    color={(color?.value as ButtonColor) || 'primary'}
                                    variant={variant?.value ? (variant.value as ButtonVariant) : undefined}
                                    size={(size?.value as ButtonSize) || 'md'}
                                    onClick={() => {}}
                                    type='button'
                                    disabled
                                >
                                    Disabled
                                </StanButton>
                            </div>

                            <StanDivider padded label='All Combinations' />

                            <div className='grid grid-cols-1 gap-6 md:grid-cols-2'>
                                {/* All Colors */}
                                <div>
                                    <h4 className='mb-3'>All Colors</h4>
                                    <div className='flex flex-wrap gap-2'>
                                        <StanButton
                                            name='button-primary'
                                            color='primary'
                                            size={(size?.value as ButtonSize) || 'md'}
                                            onClick={() => {}}
                                        >
                                            Primary
                                        </StanButton>
                                        <StanButton
                                            name='button-secondary'
                                            color='secondary'
                                            size={(size?.value as ButtonSize) || 'md'}
                                            onClick={() => {}}
                                        >
                                            Secondary
                                        </StanButton>
                                        <StanButton
                                            name='button-dark'
                                            color='dark'
                                            size={(size?.value as ButtonSize) || 'md'}
                                            onClick={() => {}}
                                        >
                                            Dark
                                        </StanButton>
                                        <StanButton
                                            name='button-destructive'
                                            color='destructive'
                                            size={(size?.value as ButtonSize) || 'md'}
                                            onClick={() => {}}
                                        >
                                            Destructive
                                        </StanButton>
                                    </div>
                                </div>

                                {/* All Sizes */}
                                <div>
                                    <h4 className='mb-3'>All Sizes</h4>
                                    <div className='flex flex-wrap items-center gap-2'>
                                        <StanButton
                                            name='button-xs'
                                            color={(color?.value as ButtonColor) || 'primary'}
                                            size='xs'
                                            onClick={() => {}}
                                        >
                                            XS
                                        </StanButton>
                                        <StanButton
                                            name='button-sm'
                                            color={(color?.value as ButtonColor) || 'primary'}
                                            size='sm'
                                            onClick={() => {}}
                                        >
                                            SM
                                        </StanButton>
                                        <StanButton
                                            name='button-md'
                                            color={(color?.value as ButtonColor) || 'primary'}
                                            size='md'
                                            onClick={() => {}}
                                        >
                                            MD
                                        </StanButton>
                                        <StanButton
                                            name='button-lg'
                                            color={(color?.value as ButtonColor) || 'primary'}
                                            size='lg'
                                            onClick={() => {}}
                                        >
                                            LG
                                        </StanButton>
                                        <StanButton
                                            name='button-xl'
                                            color={(color?.value as ButtonColor) || 'primary'}
                                            size='xl'
                                            onClick={() => {}}
                                        >
                                            XL
                                        </StanButton>
                                    </div>
                                </div>

                                {/* All Variants */}
                                <div>
                                    <h4 className='mb-3'>All Variants</h4>
                                    <div className='flex flex-wrap gap-2'>
                                        <StanButton
                                            name='button-default'
                                            color={(color?.value as ButtonColor) || 'primary'}
                                            size={(size?.value as ButtonSize) || 'md'}
                                            onClick={() => {}}
                                        >
                                            Default
                                        </StanButton>
                                        <StanButton
                                            name='button-ghost'
                                            color={(color?.value as ButtonColor) || 'primary'}
                                            variant='ghost'
                                            size={(size?.value as ButtonSize) || 'md'}
                                            onClick={() => {}}
                                        >
                                            Ghost
                                        </StanButton>
                                        <StanButton
                                            name='button-square'
                                            color={(color?.value as ButtonColor) || 'primary'}
                                            variant='square'
                                            size={(size?.value as ButtonSize) || 'md'}
                                            onClick={() => {}}
                                        >
                                            Square
                                        </StanButton>
                                        <StanButton
                                            name='button-outline'
                                            color={(color?.value as ButtonColor) || 'primary'}
                                            variant='outline'
                                            size={(size?.value as ButtonSize) || 'md'}
                                            onClick={() => {}}
                                        >
                                            Outline
                                        </StanButton>
                                        <StanButton
                                            name='button-link'
                                            color={(color?.value as ButtonColor) || 'primary'}
                                            variant='link'
                                            size={(size?.value as ButtonSize) || 'md'}
                                            onClick={() => {}}
                                        >
                                            Link
                                        </StanButton>
                                    </div>
                                </div>

                                {/* States */}
                                <div>
                                    <h4 className='mb-3'>States</h4>
                                    <div className='flex flex-wrap gap-2'>
                                        <StanButton
                                            name='button-normal-state'
                                            color={(color?.value as ButtonColor) || 'primary'}
                                            size={(size?.value as ButtonSize) || 'md'}
                                            onClick={() => {}}
                                        >
                                            Normal
                                        </StanButton>
                                        <StanButton
                                            name='button-loading-state'
                                            color={(color?.value as ButtonColor) || 'primary'}
                                            size={(size?.value as ButtonSize) || 'md'}
                                            onClick={() => {}}
                                            loading
                                        >
                                            Loading
                                        </StanButton>
                                        <StanButton
                                            name='button-disabled-state'
                                            color={(color?.value as ButtonColor) || 'primary'}
                                            size={(size?.value as ButtonSize) || 'md'}
                                            onClick={() => {}}
                                            disabled
                                        >
                                            Disabled
                                        </StanButton>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                </div>

                {/* Badges Section */}
                <div className='bg-canvas mb-8 w-full rounded-lg border-2 border-b-4 border-l-4 p-6 shadow-md'>
                    <section id='badges' className='w-full'>
                        <h3>Badges</h3>
                        <div className='mb-4 grid grid-cols-2 gap-4'>
                            <StanDropdown
                                id='badge-variant'
                                name='badge-variant'
                                options={[
                                    { label: 'Default', value: 'default' },
                                    { label: 'Primary', value: 'primary' },
                                    { label: 'Success', value: 'success' },
                                    { label: 'Destructive', value: 'destructive' },
                                    { label: 'Muted', value: 'muted' },
                                ]}
                                onSelect={setBadgeVariant}
                                label='Variant'
                            />
                            <StanDropdown
                                id='badge-size'
                                name='badge-size'
                                options={[
                                    { label: 'Small', value: 'sm' },
                                    { label: 'Medium', value: 'md' },
                                    { label: 'Large', value: 'lg' },
                                ]}
                                onSelect={setBadgeSize}
                                label='Size'
                            />
                            <StanInput
                                id='badge-text'
                                name='badge-text'
                                value={badgeText}
                                onChange={e => setBadgeText(e.target.value)}
                                topLabel='Badge Text'
                            />
                            <div className='flex flex-col gap-2'>
                                <label className='flex items-center gap-2'>
                                    <input
                                        type='checkbox'
                                        checked={badgeWithIcon}
                                        onChange={e => setBadgeWithIcon(e.target.checked)}
                                    />
                                    <span className='text-sm'>With Icon</span>
                                </label>
                                <label className='flex items-center gap-2'>
                                    <input
                                        type='checkbox'
                                        checked={badgeClickable}
                                        onChange={e => setBadgeClickable(e.target.checked)}
                                    />
                                    <span className='text-sm'>Clickable</span>
                                </label>
                            </div>
                        </div>

                        <div className='mb-6'>
                            <h4 className='mb-3'>Custom Badge</h4>
                            <div className='flex items-center gap-2'>
                                <StanBadge
                                    variant={badgeVariant.value as BadgeVariant}
                                    size={badgeSize.value as BadgeSize}
                                    icon={badgeWithIcon ? <IconLink /> : undefined}
                                    onClick={
                                        badgeClickable
                                            ? () =>
                                                  stanNotify({
                                                      title: 'Badge Clicked',
                                                      text: `${badgeText} badge was clicked`,
                                                      type: 'info',
                                                  })
                                            : undefined
                                    }
                                >
                                    {badgeText}
                                </StanBadge>
                            </div>
                        </div>

                        <StanDivider padded label='All Variants' />

                        <div className='mt-4 grid grid-cols-1 gap-6 md:grid-cols-2'>
                            {/* All Variants */}
                            <div>
                                <h4 className='mb-3'>Variants</h4>
                                <div className='flex flex-wrap gap-2'>
                                    <StanBadge variant='default'>Default</StanBadge>
                                    <StanBadge variant='primary'>Primary</StanBadge>
                                    <StanBadge variant='success'>Success</StanBadge>
                                    <StanBadge variant='destructive'>Destructive</StanBadge>
                                    <StanBadge variant='muted'>Muted</StanBadge>
                                </div>
                            </div>

                            {/* All Sizes */}
                            <div>
                                <h4 className='mb-3'>Sizes</h4>
                                <div className='flex flex-wrap items-center gap-2'>
                                    <StanBadge size='sm'>Small</StanBadge>
                                    <StanBadge size='md'>Medium</StanBadge>
                                    <StanBadge size='lg'>Large</StanBadge>
                                </div>
                            </div>

                            {/* With Icons */}
                            <div>
                                <h4 className='mb-3'>With Icons</h4>
                                <div className='flex flex-wrap gap-2'>
                                    <StanBadge variant='primary' icon={<IconLink />}>
                                        Primary
                                    </StanBadge>
                                    <StanBadge variant='success' icon={<IconLink />}>
                                        Success
                                    </StanBadge>
                                    <StanBadge variant='destructive' icon={<IconLink />}>
                                        Destructive
                                    </StanBadge>
                                </div>
                            </div>

                            {/* Clickable */}
                            <div>
                                <h4 className='mb-3'>Clickable</h4>
                                <div className='flex flex-wrap gap-2'>
                                    <StanBadge
                                        variant='primary'
                                        onClick={() =>
                                            stanNotify({
                                                title: 'Badge Clicked',
                                                type: 'success',
                                            })
                                        }
                                    >
                                        Click me
                                    </StanBadge>
                                    <StanBadge
                                        variant='success'
                                        icon={<IconLink />}
                                        onClick={() =>
                                            stanNotify({
                                                title: 'Badge with Icon Clicked',
                                                type: 'info',
                                            })
                                        }
                                    >
                                        With Icon
                                    </StanBadge>
                                </div>
                            </div>
                        </div>
                    </section>
                </div>

                {/* Checkbox Section */}
                <div className='bg-canvas mb-8 w-full rounded-lg border-2 border-b-4 border-l-4 p-6 shadow-md'>
                    <section id='checkbox'>
                        <h3>StanCheckbox</h3>
                        <div className='flex flex-col gap-y-4'>
                            <div className='flex flex-col gap-y-4'>
                                <StanDropdown
                                    id='test-dropdown-toggle'
                                    name='test-dropdown-toggle'
                                    options={[
                                        { label: 'True', value: 'true' },
                                        { label: 'False', value: 'false' },
                                    ]}
                                    onSelect={handleStanCheckboxError}
                                    label='Error'
                                />
                                <StanDropdown
                                    id='test-dropdown-toggle'
                                    name='test-dropdown-toggle'
                                    options={[
                                        { label: 'True', value: 'true' },
                                        { label: 'False', value: 'false' },
                                    ]}
                                    onSelect={handleStanCheckboxDisabled}
                                    label='Disabled'
                                />
                                <StanDropdown
                                    id='test-dropdown-toggle'
                                    name='test-dropdown-toggle'
                                    options={[
                                        { label: 'True', value: 'true' },
                                        { label: 'False', value: 'false' },
                                    ]}
                                    onSelect={handleStanCheckboxIndeterminate}
                                    label='Indeterminate'
                                />
                                <StanDropdown
                                    id='test-dropdown-toggle'
                                    name='test-dropdown-toggle'
                                    options={[
                                        { label: 'True', value: 'true' },
                                        { label: 'False', value: 'false' },
                                    ]}
                                    onSelect={handleStanCheckboxLoading}
                                    label='Loading'
                                />
                                <StanDropdown
                                    id='test-dropdown-toggle'
                                    name='test-dropdown-toggle'
                                    options={[
                                        { label: 'True', value: 'true' },
                                        { label: 'False', value: 'false' },
                                    ]}
                                    onSelect={option => setCheckValue(option.value === 'true')}
                                    label='Checked'
                                />
                            </div>
                            <div className='flex justify-center'>
                                <StanCheckbox
                                    error={(stanCheckboxError?.value.toLowerCase() === 'true') as boolean}
                                    disabled={(stanCheckboxDisabled?.value.toLowerCase() === 'true') as boolean}
                                    loading={(stanCheckboxLoading?.value.toLowerCase() === 'true') as boolean}
                                    checked={checkValue}
                                    indeterminate={
                                        (stanCheckboxIndeterminate?.value.toLowerCase() === 'true') as boolean
                                    }
                                    onChange={e => setCheckValue(e.target.checked)}
                                />
                            </div>
                            <hr />
                            <div className='flex justify-center'>
                                <div className='flex flex-col gap-y-2'>
                                    <StanCheckbox
                                        label='Default'
                                        checked={checkValueRight}
                                        onChange={e => setCheckValueRight(e.target.checked)}
                                    />
                                    <StanCheckbox
                                        label='Default caption'
                                        caption='Default indeterminate'
                                        checked={checkValueRightCaption}
                                        indeterminate
                                        onChange={e => setCheckValueRightCaption(e.target.checked)}
                                    />
                                    <StanCheckbox
                                        label='Error'
                                        placement='left'
                                        checked={checkValueLeft}
                                        error
                                        onChange={e => setCheckValueLeft(e.target.checked)}
                                    />
                                    <StanCheckbox
                                        label='Error caption'
                                        placement='left'
                                        caption='Error indeterminate'
                                        error
                                        indeterminate
                                        checked={checkValueLeftCaption}
                                        onChange={e => setCheckValueLeftCaption(e.target.checked)}
                                    />
                                </div>
                            </div>
                        </div>
                    </section>
                </div>

                {/* Draggable Section */}
                <div className='bg-canvas mb-8 rounded-lg border-2 border-b-4 border-l-4 p-6 shadow-md'>
                    <section id='draggable'>
                        <h3>StanDraggable</h3>
                        <div className='mt-4 space-y-6'>
                            {/* Normal Example */}
                            <div>
                                <h4 className='mb-3 text-lg font-semibold'>Normal Draggable Items</h4>
                                <p className='text-neutral-dimmed mb-4 text-sm'>
                                    Drag items by the handle to reorder them
                                </p>
                                <DraggableExample />
                            </div>

                            {/* Disabled State */}
                            <div>
                                <h4 className='mb-3 text-lg font-semibold'>Disabled State</h4>
                                <p className='text-neutral-dimmed mb-4 text-sm'>Disabled items cannot be dragged</p>
                                <DisabledExample />
                            </div>

                            {/* Nested Draggables */}
                            <div>
                                <h4 className='mb-3 text-lg font-semibold'>Nested Draggables</h4>
                                <p className='text-neutral-dimmed mb-4 text-sm'>
                                    Draggable items within draggable containers
                                </p>
                                <NestedDraggablesExample />
                            </div>
                        </div>
                    </section>
                </div>

                {/* StanBanner Section */}
                <div className='bg-canvas mb-8 rounded-lg border-2 border-b-4 border-l-4 p-6 shadow-md'>
                    <section id='stanbanner'>
                        <h3>StanBanner</h3>
                        <div className='mt-4 space-y-6'>
                            {/* Warning Banner with CTA */}
                            <div>
                                <h4 className='mb-3 text-lg font-semibold'>Warning Banner with CTA Link</h4>
                                <p className='text-neutral-dimmed mb-4 text-sm'>
                                    Desktop: Shows CTA link button. Mobile: Whole banner is clickable.
                                </p>
                                <StanBanner
                                    variant='warning'
                                    icon={IcAlertCircle}
                                    onClick={() => alert('Banner clicked!')}
                                    ctaText='Set Up Direct Deposit'
                                >
                                    To start selling, set up direct deposit
                                </StanBanner>
                            </div>

                            {/* Info Banner with CTA */}
                            <div>
                                <h4 className='mb-3 text-lg font-semibold'>Info Banner with CTA Link</h4>
                                <StanBanner
                                    variant='info'
                                    icon={IcInfoCircle}
                                    onClick={() => alert('Banner clicked!')}
                                    ctaText='Learn More'
                                >
                                    This is an informational banner with a call-to-action
                                </StanBanner>
                            </div>

                            {/* Danger Banner with CTA */}
                            <div>
                                <h4 className='mb-3 text-lg font-semibold'>Danger Banner with CTA Link</h4>
                                <StanBanner
                                    variant='danger'
                                    icon={IcAlertCircle}
                                    onClick={() => alert('Banner clicked!')}
                                    ctaText='Fix Now'
                                >
                                    Action required: Please resolve this issue immediately
                                </StanBanner>
                            </div>

                            {/* Banner with Title and Subtitle */}
                            <div>
                                <h4 className='mb-3 text-lg font-semibold'>Banner with Title and Subtitle</h4>
                                <StanBanner
                                    variant='warning'
                                    icon={IcAlertCircle}
                                    title='Account Setup Required'
                                    subtitle='Complete your account setup to start accepting payments'
                                    onClick={() => alert('Banner clicked!')}
                                    ctaText='Complete Setup'
                                />
                            </div>

                            {/* Banner with Buttons */}
                            <div>
                                <h4 className='mb-3 text-lg font-semibold'>Banner with Action Buttons</h4>
                                <StanBanner
                                    variant='info'
                                    icon={IcInfoCircle}
                                    title='New Feature Available'
                                    subtitle='Check out our latest updates and improvements'
                                    buttons={[
                                        {
                                            name: 'Learn More',
                                            onClick: () => alert('Learn More clicked'),
                                            variant: 'outline',
                                        },
                                        {
                                            name: 'Dismiss',
                                            onClick: () => alert('Dismiss clicked'),
                                            variant: 'ghost',
                                        },
                                    ]}
                                />
                            </div>

                            {/* Simple Text Banner */}
                            <div>
                                <h4 className='mb-3 text-lg font-semibold'>Simple Text Banner</h4>
                                <StanBanner variant='warning' onClick={() => alert('Banner clicked!')}>
                                    Simple warning message without icon or CTA
                                </StanBanner>
                            </div>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );

    return <>{internalsContent}</>;
}

type AudienceRow = {
    id: string;
    fullName: string;
    email: string;
    fanSinceDate: string;
    totalPurchases: number;
    totalSpent: number;
    totalSubscriptions: number;
};

const mockDataForTable: AudienceRow[] = [
    {
        id: '1',
        fullName: 'Bob Doe',
        email: 'bob.doe.has.a.really.long.mail@longmailaddress.com',
        fanSinceDate: '2025-07-10',
        totalPurchases: 85,
        totalSpent: 5839.85,
        totalSubscriptions: 19,
    },
    {
        id: '2',
        fullName: 'Jane Smith',
        email: 'jane.smith@example.com',
        fanSinceDate: '2022-11-03',
        totalPurchases: 3,
        totalSpent: 75.0,
        totalSubscriptions: 1,
    },
    {
        id: '3',
        fullName: 'Alice Johnson',
        email: 'alice.j@example.com',
        fanSinceDate: '2024-02-20',
        totalPurchases: 8,
        totalSpent: 200.0,
        totalSubscriptions: 3,
    },
    {
        id: '4',
        fullName: 'Michael Nguyen',
        email: 'm.nguyen92@domain.net',
        fanSinceDate: '2023-05-15',
        totalPurchases: 22,
        totalSpent: 1280.5,
        totalSubscriptions: 5,
    },
    {
        id: '5',
        fullName: 'Emily Carter',
        email: 'emilycarter88@fastmail.com',
        fanSinceDate: '2021-09-12',
        totalPurchases: 47,
        totalSpent: 3345.75,
        totalSubscriptions: 11,
    },
    {
        id: '6',
        fullName: 'Carlos Rodriguez',
        email: 'carlos.rdz@anothermail.org',
        fanSinceDate: '2025-01-03',
        totalPurchases: 5,
        totalSpent: 190.0,
        totalSubscriptions: 2,
    },
    {
        id: '7',
        fullName: 'Sara Lee',
        email: 'sara.lee123@supermail.io',
        fanSinceDate: '2023-08-24',
        totalPurchases: 16,
        totalSpent: 950.25,
        totalSubscriptions: 4,
    },
    {
        id: '8',
        fullName: 'David Kim',
        email: 'david.kim@someverylongemailprovideraddress.co',
        fanSinceDate: '2022-02-28',
        totalPurchases: 72,
        totalSpent: 4999.99,
        totalSubscriptions: 16,
    },
    {
        id: '9',
        fullName: 'William Andersen',
        email: 'wandersen@mail.com',
        fanSinceDate: '2024-03-15',
        totalPurchases: 12,
        totalSpent: 450.0,
        totalSubscriptions: 4,
    },
    {
        id: '10',
        fullName: 'Quinn Taylor',
        email: 'quinnt@mailprovider.com',
        fanSinceDate: '2023-10-05',
        totalPurchases: 30,
        totalSpent: 1200.0,
        totalSubscriptions: 8,
    },
    {
        id: '11',
        fullName: 'Olivia Brown',
        email: 'olivia.brown@mail.co.uk',
        fanSinceDate: '2025-06-01',
        totalPurchases: 10,
        totalSpent: 300.0,
        totalSubscriptions: 2,
    },
];
